import type { TextFieldProps } from "@mui/material";
import { type MaskBlockType, type MaskType, TextInput } from "Components/_Shared/Inputs/TextInput";
import { StyledFormControl } from "Forms/Components/FormStyles";
import { get } from "lodash-es";
import type { HTMLInputTypeAttribute, JSX, ReactNode } from "react";
import { type Control, Controller, type FieldErrors, type FieldPath, type FieldValues } from "react-hook-form";

type Props<FormType extends FieldValues> = Omit<TextFieldProps, "onChange"> & {
  name: FieldPath<FormType>;
  label?: string;
  characterCounter?: ReactNode;
  control: Control<FormType, object>;
  errors: FieldErrors<FormType>;
  maxLength?: number;
  disabled?: boolean;
  mask?: MaskType;
  maskBlocks?: MaskBlockType;
  type?: HTMLInputTypeAttribute;
  accept?: string;
  max?: number;
  min?: number;
  scale?: number;
  formControlClass?: "long" | "medium" | "tiny" | string;
  unit?: string;
  inputEndAdornment?: JSX.Element;
  inputStartAdornment?: JSX.Element;
  hasFilterStyle?: boolean;
  unmask?: boolean | "typed";
  thousandsSeparator?: string;
  labelEndAdornment?: JSX.Element;
  onChange?: (value: string) => void;
};

type TransformType = {
  input: (value: any) => string;
  output: (e: { target: { value: any } }) => any;
};

const getTransform = (type: HTMLInputTypeAttribute): TransformType => {
  switch (type) {
    case "number":
      return {
        input: (value: number) => (!value || Number.isNaN(value) || value === 0 ? "" : value.toString()),
        output: (e: { target: { value: string } }) => {
          const output = Number(e.target.value);
          return Number.isNaN(output) ? 0 : output;
        },
      };

    default:
      return {
        input: (value: any) => (value === null || value === undefined ? "" : String(value)),
        output: (e: { target: { value: string } }) => {
          return e.target.value;
        },
      };
  }
};

export const BlFormInput = <T extends object>(props: Props<T>) => {
  const {
    name,
    control,
    errors,
    maxLength,
    label,
    disabled,
    mask,
    type = "text",
    max,
    min,
    scale,
    formControlClass,
    unit,
    hasFilterStyle,
    thousandsSeparator,
    accept,
    labelEndAdornment,
    onBlur,
    onChange,
    ...rest
  } = props;

  const transform = getTransform(type);

  return (
    <>
      <Controller
        control={control}
        name={name}
        render={({ field: { onChange: onControlChange, value, onBlur: onControlBlur } }) => {
          return (
            <StyledFormControl className={`${formControlClass} ${!label ? "no-label" : ""}`}>
              <TextInput
                value={transform.input(value as any)}
                name={name.toString()}
                label={label}
                fieldError={get(errors, name as string)}
                maxLength={maxLength}
                disabled={disabled}
                onBlur={(e) => {
                  onControlBlur();
                  onBlur?.(e);
                }}
                mask={mask}
                type={type ?? "text"}
                accept={accept}
                max={max}
                min={min}
                scale={scale}
                unit={unit}
                onInputChange={(e) => {
                  onControlChange(e);
                  onChange?.(e.currentTarget.value);
                }}
                hasFilterStyle={hasFilterStyle}
                thousandsSeparator={thousandsSeparator}
                onMaskInputChange={(v) => {
                  onControlChange(v);
                  onChange?.(v);
                }}
                labelEndAdornment={labelEndAdornment}
                {...rest}
              />
            </StyledFormControl>
          );
        }}
      />
    </>
  );
};
